import { Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, Slide, SlideProps, Snackbar, TextField, Tooltip } from '@material-ui/core';
import type { FC } from 'react';
import React from 'react';
import type { PackageType, ProductType } from '../types';
import { Grid } from '@material-ui/core';
import Summary from '../Summary';
import ProductTabs from '../Components/ProductTabs/ProductTabs';
import { usePackage } from '../hooks/usePackage';
import { useNavigate, useParams } from 'react-router';
import Details from '../Components/Details';
import {
  getAvailableProducts,
  getExtraConfiguration,
  getHotelConfiguation,
  getInsuranceConfiguration,
  getTicketConfiguation,
  getTransferConfiguration
} from '../utils';
import { Link } from 'react-router-dom';
import PackagePayment from '../Components/PackagePayment';
import { formatMonthDayYear } from '../../../utils/utils';
import { useDeletePackageMutation } from '../graphql/deletePackage.generated';
import PackageGiftCard from '../Components/PackageGiftCard';
import { useCreatePackageConfirmationMutation } from '../graphql/createPackageConfirmation.generated';

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

type ActiveItem = {
  hotel?: string;
  ticket?: string;
  transfer?: string;
  insurance?: string;
  extra?: string;
};

const PackageDetails: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeProduct, setActiveProduct] = React.useState<ProductType>('hotel');
  const [editValues, setEditValues] = React.useState<ActiveItem>({});
  const [open, setOpen] = React.useState(false);
  const [isConfirmationModalOpened, setIsConfirmationModelOpened] = React.useState<boolean>(false);
  const [isItineraryDialogOpen, setIsItineraryDialogOpen] = React.useState(false);
  const [itinerary, setItinerary] = React.useState('');
  const [createPackageConfirmation] = useCreatePackageConfirmationMutation();
  const { packageDetails, state, error } = usePackage(id || '');
  const [deletePackage] = useDeletePackageMutation({
    onCompleted: ({ deletePackage }) => {
      if (deletePackage) {
        navigate('/packages');
      } else {
        setOpen(true);
      }
    }
  });
  console.log({ error });
  const handleClose = () => {
    setOpen(false);
  };

  const handleDeletePackage = async () => {
    if (id) {
      await deletePackage({ variables: { packageId: id } });
    }
  };

  const handleOnDeleteClick = () => {
    setIsConfirmationModelOpened(true);
  };

  const handleSummaryItemClick = async (id: string, productType: ProductType) => {
    setActiveProduct(productType);
    setEditValues({ [productType]: id });
  };

  const renderHotel = (hotel: PackageType['hotels'][0]) => {
    return (
      <Details config={getHotelConfiguation(hotel)} title={hotel?.hotel?.name || 'Hotel'}>
        <PackagePayment key={hotel?.partnerPayment?.id} payment={hotel?.partnerPayment!} />
      </Details>
    );
  };

  const renderTicket = (ticket: PackageType['tickets'][0]) => {
    return (
      <Details config={getTicketConfiguation(ticket)} title={ticket?.company}>
        <PackagePayment key={ticket?.partnerPayment?.id} payment={ticket?.partnerPayment!} />
      </Details>
    );
  };

  const renderTransfer = (transfer: PackageType['transfers'][0]) => {
    return (
      <Details config={getTransferConfiguration(transfer)} title={transfer?.company}>
        <PackagePayment key={transfer?.partnerPayment?.id} payment={transfer?.partnerPayment!} />
      </Details>
    );
  };

  const renderInsurance = (insurance: PackageType['insurances'][0]) => {
    return (
      <Details config={getInsuranceConfiguration(insurance)} title={insurance?.company}>
        <PackagePayment key={insurance?.partnerPayment?.id} payment={insurance?.partnerPayment!} />
      </Details>
    );
  };

  const renderExtra = (extra: PackageType['extras'][0]) => {
    return (
      <Details config={getExtraConfiguration(extra)} title={extra?.name}>
        <PackagePayment key={extra?.partnerPayment?.id} payment={extra?.partnerPayment!} />
      </Details>
    );
  };

  const renderProductForm = () => {
    if (activeProduct) {
      if (activeProduct === 'hotel') {
        const values = packageDetails?.hotels.find((hotel) => editValues.hotel === hotel.id);

        if (values) {
          return <>{renderHotel(values)}</>;
        } else {
          return <div className="space-y-4">{packageDetails?.hotels?.map(renderHotel)}</div>;
        }
      }

      if (activeProduct === 'ticket') {
        const values = packageDetails?.tickets.find((ticket) => editValues.ticket === ticket.id);

        if (values) {
          return renderTicket(values);
        } else {
          return <div className="space-y-4">{packageDetails?.tickets?.map(renderTicket)}</div>;
        }
      }

      if (activeProduct === 'transfer') {
        const values = packageDetails?.transfers.find((transfer) => editValues.transfer === transfer.id);

        if (values) {
          return renderTransfer(values);
        } else {
          return <div className="space-y-4">{packageDetails?.transfers?.map(renderTransfer)}</div>;
        }
      }

      if (activeProduct === 'insurance') {
        const values = packageDetails?.insurances.find((insurance) => editValues.insurance === insurance.id);

        if (values) {
          return renderInsurance(values);
        } else {
          return <div className="space-y-4">{packageDetails?.insurances?.map(renderInsurance)}</div>;
        }
      }

      if (activeProduct === 'extra') {
        const values = packageDetails?.extras.find((extra) => editValues.extra === extra.id);

        if (values) {
          return renderExtra(values);
        } else {
          return <div className="space-y-4">{packageDetails?.extras?.map(renderExtra)}</div>;
        }
      }
    }
  };

  const resetStates = () => {
    setEditValues({});
  };

  const handleTabChange = (value: ProductType) => {
    setActiveProduct(value);
    resetStates();
  };

  const handleDownloadConfirmation = async () => {
    const result = await createPackageConfirmation({ variables: { packageId: id!, itinerary } });
    if (result.data?.createPackageConfirmation) {
      const win = window.open('', 'document');
      win?.document.write(result.data.createPackageConfirmation);
    }
    setIsItineraryDialogOpen(false);
    setItinerary('');
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="my-6 space-y-2">
          <h1 className="text-3xl font-bold mb-2">{packageDetails?.packageName || 'Package details'}</h1>
          {packageDetails?.startDate && packageDetails?.endDate && (
            <div className="text-lg text-gray-500">
              From: {formatMonthDayYear(+packageDetails?.startDate)} Until: {formatMonthDayYear(+packageDetails?.endDate)}
            </div>
          )}
          {packageDetails?.createdAt && (
            <div className="text-sm text-gray-500">
              Created at: {formatMonthDayYear(+packageDetails?.createdAt)} by {packageDetails?.createdBy?.firstName}{' '}
              {packageDetails?.createdBy?.lastName}
            </div>
          )}
        </div>
        <div className="flex space-x-2 items-center">
          <Button disabled={!packageDetails?.customerPayment?.total?.amount} variant="outlined" onClick={() => setIsItineraryDialogOpen(true)}>
            Confirmation
          </Button>
          <Link to={`/packages/${id}/edit`}>
            <Button color="primary" variant="outlined">
              Update package
            </Button>
          </Link>
          <Button onClick={handleOnDeleteClick} color="secondary" variant="outlined">
            Delete package
          </Button>
        </div>
      </div>
      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <ProductTabs
            availableProducts={packageDetails ? getAvailableProducts(packageDetails) : undefined}
            value={activeProduct}
            setValue={handleTabChange}
          />
          <div className="mt-8">{renderProductForm()}</div>
        </Grid>
        <Grid item xs={12} md={5}>
          <div>
            <Summary onItemClick={handleSummaryItemClick} packageState={state} />
            <Card className="mt-8 py-2 border border-gray-300 flex flex-col">
              <div className="flex items-center justify-between text-lg font-bold px-3 py-2">
                <span>Customer: </span>
                <Link className="text-lg text-blue-800 hover:underline" to={`/customers/details/${packageDetails?.customer?.id}`}>
                  {packageDetails?.customer?.firstName} {packageDetails?.customer?.lastName}
                </Link>
              </div>
            </Card>
            <PackageGiftCard entityId={packageDetails?.id || ''} />
            {packageDetails?.customerPayment && (
              <Card className="mt-8 px-3 py-2 border border-gray-300 flex flex-col">
                <PackagePayment title="Customer payments" payment={packageDetails?.customerPayment} />
              </Card>
            )}
          </div>
        </Grid>
      </Grid>
      <Snackbar
        open={open}
        TransitionComponent={SlideTransition}
        onClose={handleClose}
        autoHideDuration={3000}
        message="Error deleting the package"
      />
      <Dialog open={isConfirmationModalOpened}>
        <DialogTitle>Are you sure you want to delete this package?</DialogTitle>
        <DialogActions>
          <Button variant="contained" onClick={handleDeletePackage} color="primary">
            Yes, I want to delete
          </Button>
          <Button onClick={() => setIsConfirmationModelOpened(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={isItineraryDialogOpen} fullWidth maxWidth="md">
        <DialogTitle>Generate Package Confirmation</DialogTitle>
        <DialogContent>
          <TextField
            label="Itinerary"
            multiline
            rows={12}
            fullWidth
            variant="outlined"
            value={itinerary}
            onChange={(e) => setItinerary(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setIsItineraryDialogOpen(false); setItinerary(''); }}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleDownloadConfirmation}>
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </div >
  );
};

export default PackageDetails;
